import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import { getJobQueues, getPolicyReviewJobs } from '../../Api/AiModels';
import { getIngestionJobs } from '../../Api/IngestApi';

export const REFRESH_MS = 10000;

const STATE = {
  idle: { label: 'Idle', bg: '#F3F4F6', color: '#374151' },
  working: { label: 'Working', bg: '#DBEAFE', color: '#1D40AE' },
  stalled: { label: 'Stalled', bg: '#FEE2E2', color: '#991B1B' },
};
const JOB_STATUS = {
  pending: { bg: '#FEF3C7', color: '#78350F' },
  processing: { bg: '#DBEAFE', color: '#1D40AE' },
  running: { bg: '#DBEAFE', color: '#1D40AE' },
  completed: { bg: '#D1FAE5', color: '#065F46' },
  partial: { bg: '#FEF3C7', color: '#78350F' },
  failed: { bg: '#FEE2E2', color: '#991B1B' },
};
const PHASE = { legal: 'Legal', expert: 'Expert', review: 'Review', render: 'Report' };

/** "just now", "4 min ago", "2 h 5 min ago", "3 d ago"; "never" without a time. */
export function ago(iso, now = Date.now()) {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return 'never';
  const mins = Math.floor(Math.max(0, now - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} h${mins % 60 ? ` ${mins % 60} min` : ''} ago`;
  return `${Math.floor(h / 24)} d ago`;
}

const StatusChip = ({ status }) => {
  const tone = JOB_STATUS[status] || { bg: '#F3F4F6', color: '#374151' };
  return <Chip size="small" label={status || '—'} sx={{ backgroundColor: tone.bg, color: tone.color, fontWeight: 600, fontSize: 11, height: 20 }} />;
};

function QueueCard({ queue }) {
  const tone = STATE[queue.state] || STATE.idle;
  const c = queue.counts || {};
  return (
    <Paper variant="outlined" data-testid={`queue-${queue.key}`} sx={{ p: 2, borderRadius: 2, flex: '1 1 320px', borderColor: queue.state === 'stalled' ? '#FCA5A5' : '#E5E7EB' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, color: '#111827' }}>{queue.label}</Typography>
        <Chip size="small" label={tone.label} sx={{ backgroundColor: tone.bg, color: tone.color, fontWeight: 600, fontSize: 11, height: 20 }} />
      </Box>
      <Typography variant="body2" sx={{ color: '#374151', mt: 1 }}>
        {c.pending || 0} waiting · {c.processing || 0} running · {c.failed_24h || 0} failed in the last 24 h
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
        Oldest waiting job: {queue.oldest_pending_at ? `queued ${ago(queue.oldest_pending_at)}` : 'none'} · Last worker activity: {ago(queue.last_activity_at)}
      </Typography>
      <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}>Worker: {queue.worker}</Typography>
      {queue.state === 'stalled' ? (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Nothing is picking up this queue. Jobs have waited since {new Date(queue.oldest_pending_at).toLocaleString()} with no worker activity. Check that the {queue.worker} Deployment is running.
        </Alert>
      ) : null}
    </Paper>
  );
}

const head = (cols) => (
  <TableHead>
    <TableRow>
      {cols.map((h) => <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12, backgroundColor: '#F9FAFB', color: '#374151', whiteSpace: 'nowrap' }}>{h}</TableCell>)}
    </TableRow>
  </TableHead>
);
const cell = { fontSize: 12, color: '#374151' };
const empty = (cols, text) => <TableRow><TableCell colSpan={cols} sx={{ textAlign: 'center', py: 4, color: '#9CA3AF' }}>{text}</TableCell></TableRow>;

function ReviewTable({ items }) {
  const cols = ['Document', 'Organisation', 'Type · Country', 'Status', 'Stage', 'Queued', 'Retries', 'Error'];
  return (
    <Table size="small" stickyHeader aria-label="Policy reviews">
      {head(cols)}
      <TableBody>
        {!items.length ? empty(cols.length, 'No policy reviews yet') : items.map((r) => (
          <TableRow key={r.review_id}>
            <TableCell sx={{ ...cell, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.filename || r.review_id}>{r.filename || r.review_id}</TableCell>
            <TableCell sx={cell} title={r.organization_id}>{r.company_name || r.organization_id}</TableCell>
            <TableCell sx={cell}>{[String(r.doc_type || '').replace(/_/g, ' '), r.country].filter(Boolean).join(' · ')}</TableCell>
            <TableCell><StatusChip status={r.status} /></TableCell>
            <TableCell sx={cell}>{r.phase ? `${PHASE[r.phase] || r.phase}${r.job_status ? ` (${r.job_status})` : ''}` : '—'}</TableCell>
            <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{ago(r.created_at)}</TableCell>
            <TableCell sx={cell}>{r.retry_count || '—'}</TableCell>
            <TableCell sx={{ ...cell, color: '#991B1B', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.error || ''}>{r.error || ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function IngestionTable({ items }) {
  const cols = ['File', 'Organisation', 'Type', 'Status', 'Stage', 'Queued', 'Retries', 'Error'];
  return (
    <Table size="small" stickyHeader aria-label="Document ingestion">
      {head(cols)}
      <TableBody>
        {!items.length ? empty(cols.length, 'No waiting, running or recently failed ingestion jobs') : items.map((j) => (
          <TableRow key={j.job_id}>
            <TableCell sx={{ ...cell, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.filename || j.file_id}>{j.filename || j.file_id}</TableCell>
            <TableCell sx={{ ...cell, fontFamily: 'monospace' }} title={j.organization_id}>{String(j.organization_id || '').slice(0, 8)}</TableCell>
            <TableCell sx={cell}>{j.doc_type || '—'}</TableCell>
            <TableCell><StatusChip status={j.status} /></TableCell>
            <TableCell sx={cell}>{j.stage || '—'}</TableCell>
            <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{ago(j.created_at)}</TableCell>
            <TableCell sx={cell}>{j.retry_count || '—'}</TableCell>
            <TableCell sx={{ ...cell, color: '#991B1B', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.error_message || ''}>{j.error_message || ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Ingestion rows to show: running first, then waiting, then the last 20 failures (as the Search Index Explorer queue does). */
async function loadIngestion() {
  const [processing, pending, failed] = await Promise.all([
    getIngestionJobs({ status: 'processing', limit: 100 }),
    getIngestionJobs({ status: 'pending', limit: 100 }),
    getIngestionJobs({ status: 'failed', limit: 20 }),
  ]);
  return [processing, pending, failed].flatMap((res) => res?.data?.items || []);
}

/**
 * Background jobs across every organisation: a health card per queue (document ingestion, policy
 * reviews) that turns red when work waits with no worker picking it up, and the jobs behind each.
 * Refreshes every 10 s. Platform admins only (the AI backend checks).
 */
const TrinityJobs = ({ loadQueues = getJobQueues, loadReviews = getPolicyReviewJobs, loadJobs = loadIngestion }) => {
  const [queues, setQueues] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [updated, setUpdated] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [q, r, j] = await Promise.all([loadQueues(), loadReviews({ limit: 100 }), loadJobs()]);
      setQueues(Array.isArray(q?.data?.queues) ? q.data.queues : []);
      setReviews(Array.isArray(r?.data?.items) ? r.data.items : []);
      setJobs(Array.isArray(j) ? j : []);
      setUpdated(new Date());
      setError('');
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Tech Admin only' : 'Could not load the job queues');
    }
  }, [loadQueues, loadReviews, loadJobs]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Background jobs</Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, mb: 2 }}>
        Document ingestion and policy reviews for every organisation. A queue turns red when jobs wait with no worker picking them up.
        {updated ? ` Updated ${updated.toLocaleTimeString()}, refreshes every ${REFRESH_MS / 1000} s.` : ''}
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {queues.map((q) => <QueueCard key={q.key} queue={q} />)}
      </Box>
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`Policy reviews (${reviews.length})`} sx={{ textTransform: 'none' }} />
        <Tab label={`Document ingestion (${jobs.length})`} sx={{ textTransform: 'none' }} />
      </Tabs>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          {tab === 0 ? <ReviewTable items={reviews} /> : <IngestionTable items={jobs} />}
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TrinityJobs;
